import type { Instrument } from '@dabba/contracts';

/** "2026-08-11" → "11 Aug". */
export function shortExpiry(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${d.toLocaleString('en-IN', { month: 'short' })}`;
}

/**
 * Broker-style display name for any instrument: contracts read as
 * "NIFTY 24000 CE" / "RELIANCE FUT", never the raw tradingsymbol soup.
 */
export function instrumentTitle(symbol: string, meta?: Instrument | null): string {
  if (meta?.kind === 'option' && meta.strike && meta.optionType) {
    return `${meta.name} ${meta.strike} ${meta.optionType}`;
  }
  if (meta?.kind === 'future') return `${meta.name} FUT`;
  return symbol;
}

/** Calendar days from today (IST) to expiry; negative once passed. */
export function daysToExpiry(expiry: string | undefined, now = Date.now()): number | null {
  if (!expiry) return null;
  const d = new Date(`${expiry}T00:00:00+05:30`);
  if (Number.isNaN(d.getTime())) return null;
  const istToday = new Date(new Date(now).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
  return Math.round((d.getTime() - istToday.getTime() - 5.5 * 3600_000) / 86_400_000);
}

/** List sub-line for a contract: "26 Aug · 12d left". */
export function expiryCountdown(meta?: Instrument | null, now = Date.now()): string | null {
  if (!meta || (meta.kind !== 'option' && meta.kind !== 'future')) return null;
  const date = shortExpiry(meta.expiry);
  if (!date) return null;
  const days = daysToExpiry(meta.expiry, now);
  if (days == null) return date;
  if (days < 0) return `${date} · expired`;
  if (days === 0) return `${date} · expires today`;
  return `${date} · ${days}d left`;
}

/** True when expiry is near enough that the countdown reads as a warning. */
export function expirySoon(meta?: Instrument | null, now = Date.now()): boolean {
  if (!meta || (meta.kind !== 'option' && meta.kind !== 'future')) return false;
  const days = daysToExpiry(meta.expiry, now);
  return days != null && days <= 2;
}

/** Sub-line: exchange + kind (+ expiry and lot for contracts). */
export function instrumentSub(meta?: Instrument | null): string | null {
  if (!meta) return null;
  const exp = shortExpiry(meta.expiry);
  const lot = meta.lotSize && meta.lotSize > 1 ? ` · Lot ${meta.lotSize}` : '';
  switch (meta.kind) {
    case 'index':
      return `${meta.exchange} · Index`;
    case 'future':
      return `${meta.exchange} · Futures${exp ? ` · ${exp}` : ''}${lot}`;
    case 'option':
      return `${meta.exchange} · Options${exp ? ` · ${exp}` : ''}${lot}`;
    default:
      /*
       * Plain NSE equity says nothing, so it says nothing.
       *
       * "NSE · Equity" under every row was true and useless: on a watchlist,
       * a movers list or a search result it repeated down sixteen rows and
       * distinguished none of them. What a sub-line is FOR is the row that
       * differs — a BSE listing, a future, an option and its expiry — and
       * those are exactly the cases above, which still say so. Returning
       * null here lets the row close up to one line and makes the F&O rows
       * findable by the fact that they alone carry a second line.
       */
      return meta.exchange === 'NSE' ? null : `${meta.exchange} · Equity`;
  }
}

/**
 * True once an F&O contract's expiry has passed (IST). An expired option or
 * future no longer trades — it must not sit on a watchlist showing a frozen
 * price, and the engine settles any position in it at the 15:30 close.
 */
export function isExpiredContract(meta?: Instrument | null, now = Date.now()): boolean {
  if (!meta || (meta.kind !== 'option' && meta.kind !== 'future')) return false;
  const days = daysToExpiry(meta.expiry, now);
  return days != null && days < 0;
}

/** Fallback kind classification from the tradingsymbol shape alone. */
export function kindFromSymbol(symbol: string): 'option' | 'future' | 'equity' {
  if (/\d(CE|PE)$/.test(symbol) || /\s(CE|PE)$/.test(symbol)) return 'option';
  if (/FUT$/.test(symbol)) return 'future';
  return 'equity';
}

export function productLabel(product: string): string {
  return product === 'MIS' ? 'Intraday' : product === 'CNC' ? 'Delivery' : 'Carry';
}

/** Display label for an order type — 'SL' reads as 'SL-L' in the UI. */
export function orderTypeLabel(t: string): string {
  return t === 'SL' ? 'SL-L' : t;
}
